import React, { useCallback } from "react";
import { Create } from "@refinedev/antd";
import { useNavigation } from "@refinedev/core";
import { Form, Card, Space, Button, message, Alert } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { SourcingStepper } from "./components/SourcingStepper";
import { TypeScopeSelector } from "./components/TypeScopeSelector";
import { BidForm } from "./components/BidForm";
import { SourcingReviewStep } from "./components/SourcingReviewStep";
import { AttachmentsStep } from "./components/AttachmentsStep";
import { useCreateSourcing } from "./hooks/useCreateSourcing";
import { SourcingType, SourcingScope, TechnicalEvaluationMode } from "./interface";

export const SourcingCreate: React.FC = () => {
  const { goBack } = useNavigation();
  const [form] = Form.useForm();

  const {
    currentStep,
    formData,
    loading,
    updateFormData,
    updateBidDetails,
    nextStep,
    prevStep,
    goToStep,
    submit,
    canProceedToNext,
  } = useCreateSourcing();

  const steps = [
    {
      title: "Bid Details",
      description: formData.totalAmount ? `Configure items and details - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Configure items and details",
      icon: undefined,
    },
    {
      title: "Source Type & Scope",
      description: formData.totalAmount ? `Select sourcing type and scope - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Select sourcing type and scope",
      icon: undefined,
    },
    {
      title: "Additional Informations",
      description: formData.totalAmount ? `Upload supporting documents and evaluation criteria - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Upload supporting documents and evaluation criteria",
      icon: undefined,
    },
    {
      title: "Review & Approval",
      description: formData.totalAmount ? `Review sourcing details and select approval flow - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Review sourcing details and select approval flow",
      icon: undefined,
    },
  ];

  const handleTypeChange = (type: SourcingType) => {
    updateFormData({ type });
  };

  const handleScopeChange = (sourcingScope: SourcingScope) => {
    updateFormData({ sourcingScope });
  };

  const handleTechnicalEvaluationModeChange = (mode: TechnicalEvaluationMode) => {
    updateFormData({ technicalEvaluationMode: mode });
  };

  const handleTechnicalEvaluationThresholdChange = (threshold: number | undefined) => {
    updateFormData({ technicalEvaluationThreshold: threshold });
  };



  const handlePrChange = useCallback((prIds: string[]) => {
    updateFormData({ prIds });
  }, [updateFormData]);

  const handleBidChange = useCallback((bidDetails: any) => {
    updateBidDetails(bidDetails);
  }, [updateBidDetails]);

  const handleApprovalChange = useCallback((approvalData: any) => {
    updateFormData({
      approvalType: approvalData.approvalType,
      steps: approvalData.steps,
      externalWorkflow: approvalData.externalWorkflow,
    });
  }, [updateFormData]);

  const handleSaveDraft = async () => {
    try {
      await submit();
      message.success("Sourcing saved as draft");
      goBack();
    } catch (error: any) {
      message.error(error.message || "Failed to save draft");
    }
  };

  const handleSubmit = async () => {
    try {
      await submit();
      message.success("Sourcing created successfully");
      goBack();
    } catch (error: any) {
      message.error(error.message || "Failed to create sourcing");
    }
  };

  const renderFooterButtons = () => {
    const buttons = [];

    buttons.push(
      <Button key="draft" onClick={handleSaveDraft} icon={<SaveOutlined />} loading={loading}>
        Save as Draft
      </Button>
    );

    if (currentStep > 0) {
      buttons.push(
        <Button key="prev" onClick={prevStep}>
          Previous
        </Button>
      );
    }

    if (currentStep < 3) {
      buttons.push(
        <Button key="next" type="primary" onClick={nextStep} disabled={!canProceedToNext}>
          Next
        </Button>
      );
    }

    if (currentStep === 4) {
      buttons.push(
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!canProceedToNext}
        >
          Save and Submit Sourcing
        </Button>
      );
    }

    return <Space>{buttons}</Space>;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card title="Step 1: Configure Bid Details" style={{ marginTop: 16 }}>
            {!!formData.totalAmount && (
              <Alert
                message={`Total Amount: ${formData.totalAmount} ${formData.currency || ''}`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <BidForm
              value={{
                items: formData.items,
                totalAmount: formData.totalAmount,
                currency: formData.currency,
                notes: formData.notes,
                title: formData.title,
                description: formData.description
              }}
              onChange={handleBidChange}
              selectedPrIds={formData.prIds}
              onPrChange={handlePrChange}
            />
          </Card>
        );
      case 1:
        return (
          <Card title="Step 2: Select Source Type & Scope" style={{ marginTop: 16 }}>
            {formData.totalAmount && (
              <Alert
                message={`Total Amount: ${formData.totalAmount} ${formData.currency || ''}`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <TypeScopeSelector
              typeValue={formData.type}
              scopeValue={formData.sourcingScope}
              technicalEvaluationMode={formData.technicalEvaluationMode}
              technicalEvaluationThreshold={formData.technicalEvaluationThreshold}
              onTypeChange={handleTypeChange}
              onScopeChange={handleScopeChange}
              onTechnicalEvaluationModeChange={handleTechnicalEvaluationModeChange}
              onTechnicalEvaluationThresholdChange={handleTechnicalEvaluationThresholdChange}
            />
          </Card>
        );
      case 2:
        return (
          <AttachmentsStep
            attachments={formData.attachments}
            onAttachmentsChange={(attachments) => {
              updateFormData({ attachments });
            }}
            type={formData.type}
            technicalEvaluationMode={formData.technicalEvaluationMode}
            evaluationCriterias={formData.evaluationCriterias}
            onEvaluationCriteriasChange={(evaluationCriterias) => {
              updateFormData({ evaluationCriterias });
            }}
          />
        );
      case 3:
        return (
          <Card title="Step 4: Review & Approval" style={{ marginTop: 16 }}>
            <SourcingReviewStep
              formData={formData}
              onApprovalChange={handleApprovalChange}
            />
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <Create
      title="Create New Sourcing"
      headerProps={{
        extra: (
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
              Back
            </Button>
          </Space>
        ),
      }}
      footerButtons={renderFooterButtons()}
    >
      <Form form={form} layout="vertical">
        <SourcingStepper
          currentStep={currentStep}
          onStepChange={goToStep}
          completedSteps={[]}
          steps={steps}
        />

        {renderStepContent()}
      </Form>
    </Create>
  );
};